// Export utilities used by ShippingReport & PortReport.

// Provide two named functions: exportExcelSection, exportPDFSection

// Usage: import { exportExcelSection, exportPDFSection } from '.../common/ExportUtils';



function sanitizeFilename(name) {

  return (name || 'export')

    .replace(/[^a-z0-9_\-\.]+/gi, '_')

    .replace(/_+/g, '_')

    .replace(/^_|_$/g, '');

}



function toPlainArray(data) {

  if (!Array.isArray(data)) return [];

  return data.map(r => {

    if (r && typeof r === 'object') return { ...r };

    return { value: r };

  });

}



function normalizeValue(v) {

  if (v == null) return '';

  if (Array.isArray(v)) return v.join(' | ').toUpperCase();

  if (typeof v === 'object') return JSON.stringify(v).toUpperCase();

  return String(v).toUpperCase();

}



/* ----- CSV FALLBACK HELPERS ----- */

function arrayToCSV(rows) {

  if (!rows.length) return '';

  const headers = Object.keys(rows[0]);

  const esc = s => {

    if (s == null) return '';

    const str = String(s).replace(/"/g, '""');

    return /[",\n]/.test(str) ? `"${str}"` : str;

  };

  const lines = [

    headers.map(esc).join(','),

    ...rows.map(r => headers.map(h => esc(r[h])).join(','))

  ];

  return lines.join('\n');

}



function downloadBlob(content, mime, filename) {

  const blob = new Blob([content], { type: mime });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');

  a.href = url;

  a.download = sanitizeFilename(filename);

  document.body.appendChild(a);

  a.click();

  setTimeout(() => {

    a.remove();

    URL.revokeObjectURL(url);

  }, 80);

}



/* ----- EXCEL (XLSX) ----- */

export function exportExcelSection(rawData, filename = 'ReportExport', options = {}) {

  const data = toPlainArray(rawData);

  if (!data.length) {

    alert('No data to export.');

    return;

  }

  const excludeColumns = (options && options.excludeColumns) || [];



  // Ensure all rows have same columns: collect unique keys

  const columns = Array.from(

    data.reduce((set, row) => {

      Object.keys(row).forEach(k => {
        if (!excludeColumns.includes(k)) set.add(k);
      });

      return set;

    }, new Set())

  );



  // Flatten & normalize values

  const normalized = data.map(r => {

    const obj = {};

    columns.forEach(c => (obj[c] = normalizeValue(r[c])));

    return obj;

  });



  // Try SheetJS if available (expect window.XLSX)

  const XLSX = (typeof window !== 'undefined' && window.XLSX) ? window.XLSX : null;

  if (XLSX) {

    try {

      const ws = XLSX.utils.json_to_sheet(normalized, { header: columns });

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'DATA');

      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      downloadBlob(

        out,

        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

        `${filename}.xlsx`

      );

      return;

    } catch (e) {

      console.warn('XLSX export failed, falling back to CSV', e);

    }

  }



  // Fallback CSV

  const csv = arrayToCSV(normalized);

  downloadBlob(csv, 'text/csv;charset=utf-8;', `${filename}.csv`);

}



/* ----- PDF ----- */

export function exportPDFSection(rawData, filename = 'ReportExport', options = {}) {

  const excludeColumns = (options && options.excludeColumns) || [];
  const sections = (options && options.sections) || null;

  if (sections && Array.isArray(sections) && sections.length) {
    return exportPDFWithSections(sections, filename);
  }

  const data = toPlainArray(rawData);

  if (!data.length) {

    alert('No data to export.');

    return;

  }



  // Harmonize headers

  const headers = Array.from(

    data.reduce((set, row) => {

      Object.keys(row || {}).forEach(k => {
        if (!excludeColumns.includes(k)) set.add(k);
      });

      return set;

    }, new Set())

  );



  const rows = data.map(r => headers.map(h => normalizeValue(r[h])));



  // Try jsPDF + autoTable

  const jsPDFCtor =

    (window && window.jspdf && window.jspdf.jsPDF) ||

    (window && window.jsPDF) ||

    null;



  if (jsPDFCtor) {

    try {

      const doc = new jsPDFCtor({ orientation: 'l', unit: 'pt', format: 'a4' });



      const hasAutoTable = !!doc.autoTable;

      if (hasAutoTable) {

        doc.autoTable({

          head: [headers.map(h => String(h).toUpperCase())],

          body: rows,

          styles: {

            fontSize: 7,

            cellPadding: 3,

            overflow: 'linebreak',

            valign: 'top'

          },

          headStyles: { fillColor: [40, 40, 40], fontSize: 8 },

          startY: 36,

          margin: 24,

          didDrawPage: (d) => {

            doc.setFontSize(10);

            doc.text(filename, 24, 20);

            const pageStr = `Page ${d.pageNumber}`;

            const pageWidth = doc.internal.pageSize.getWidth();

            doc.text(pageStr, pageWidth - 24 - doc.getTextWidth(pageStr), 20);

          }

        });

      } else {

        // Manual fallback table

        doc.setFontSize(10);

        doc.text(filename, 24, 20);

        let y = 40;

        const lineHeight = 12;

        const maxWidth = doc.internal.pageSize.getWidth() - 48;

        const headerLine = headers.join(' | ');

        doc.setFont(undefined, 'bold');

        doc.text(headerLine, 24, y, { maxWidth });

        doc.setFont(undefined, 'normal');

        y += lineHeight;

        rows.forEach(r => {

          const line = r.join(' | ');

            const split = doc.splitTextToSize(line, maxWidth);

          if (y + split.length * lineHeight > doc.internal.pageSize.getHeight() - 40) {

            doc.addPage();

            y = 40;

          }

          split.forEach(chunk => {

            doc.text(chunk, 24, y);

            y += lineHeight;

          });

        });

      }



      doc.save(`${sanitizeFilename(filename)}.pdf`);

      return;

    } catch (e) {

      console.warn('PDF export error, fallback to HTML print', e);

    }

  }



  // Browser fallback printable HTML

  const tableHTML = `

    <table style="border-collapse:collapse;font-family:Arial;font-size:12px;">

      <thead>

        <tr>

          ${headers

            .map(

              h =>

                `<th style="border:1px solid #ccc;padding:4px;background:#f3f3f3;text-align:left;">${h}</th>`

            )

            .join('')}

        </tr>

      </thead>

      <tbody>

        ${rows

          .map(

            row =>

              `<tr>${row

                .map(

                  v =>

                    `<td style="border:1px solid #ccc;padding:4px;vertical-align:top;">${String(

                      v

                    )

                      .replace(/</g, '&lt;')

                      .replace(/\n/g, '<br/>')}</td>`

                )

                .join('')}</tr>`

          )

          .join('')}

      </tbody>

    </table>

  `;

  const w = window.open('', '_blank');

  if (w) {

    w.document.write(`

      <html>

        <head>

          <title>${filename}</title>

          <meta charset="utf-8" />

        </head>

        <body style="margin:16px;">

          <h3 style="font-family:Arial;">${filename}</h3>

          ${tableHTML}

          <script>window.print && window.print();</script>

        </body>

      </html>

    `);

    w.document.close();

  } else {

    alert('Popup blocked. Unable to open PDF fallback.');

  }

}



// Optional helper if you want a limited column export

function exportPDFWithSections(sections, filename = 'ReportExport') {

  const jsPDFCtor =
    (window && window.jspdf && window.jspdf.jsPDF) ||
    (window && window.jsPDF) ||
    null;

  if (!jsPDFCtor) {
    alert('PDF export is not available.');
    return;
  }

  try {
    const doc = new jsPDFCtor({ orientation: 'l', unit: 'pt', format: 'a4' });
    let startY = 36;
    const margin = 24;

    sections.forEach((section, index) => {
      const data = toPlainArray(section.data || []);
      if (!data.length) return;
      const excludeColumns = (section.excludeColumns) || [];
      const headers = Array.from(
        data.reduce((set, row) => {
          Object.keys(row || {}).forEach(k => {
            if (!excludeColumns.includes(k)) set.add(k);
          });
          return set;
        }, new Set())
      );
      const rows = data.map(r => headers.map(h => normalizeValue(r[h])));

      if (section.title) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(String(section.title).toUpperCase(), margin, startY);
        doc.setFont(undefined, 'normal');
        startY += 18;
      }

      if (doc.autoTable) {
        doc.autoTable({
          head: [headers.map(h => String(h).toUpperCase())],
          body: rows,
          styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak', valign: 'top' },
          headStyles: { fillColor: [40, 40, 40], fontSize: 8 },
          startY: startY,
          margin: margin,
          didDrawPage: (d) => {
            doc.setFontSize(10);
            doc.text(filename, margin, 20);
            const pageStr = `Page ${d.pageNumber}`;
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.text(pageStr, pageWidth - margin - doc.getTextWidth(pageStr), 20);
          }
        });
        startY = doc.lastAutoTable.finalY + 24;
      }
    });

    doc.save(`${sanitizeFilename(filename)}.pdf`);
  } catch (e) {
    console.warn('Sectioned PDF export error', e);
    alert('Failed to generate PDF.');
  }
}



export function exportSelectedColumns(data, columns, filename = 'ReportExport_Selected') {

  if (!Array.isArray(data) || !data.length) {

    alert('No data to export.');

    return;

  }

  const cols = (columns || []).filter(Boolean);

  if (!cols.length) {

    exportExcelSection(data, filename);

    return;

  }

  const trimmed = data.map(row => {

    const o = {};

    cols.forEach(c => (o[c] = normalizeValue(row[c])));

    return o;

  });

  exportExcelSection(trimmed, filename);

}